// client/src/pages/Home.js
import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Button,
  Box,
  Paper,
  IconButton
} from '@mui/material';
import { Link } from 'react-router-dom';
import { 
  CheckCircle,
  ArrowForward,
  NavigateNext,
  NavigateBefore
} from '@mui/icons-material';
import { allServices, galleryImages } from '../data/ServicesData';

const carouselImages = [
  { src: '/images/hero/hero1.jpg', title: 'Expertise Juridique', subtitle: 'Une équipe à votre écoute 7j/7' },
  { src: '/images/hero/hero2.jpg', title: 'Services Immobiliers', subtitle: 'Achat, vente, gestion - Partout au Togo' },
  { src: '/images/hero/hero3.jpg', title: 'Recouvrement de Créances', subtitle: 'Solutions rapides et efficaces' },
  { src: '/images/hero/hero4.jpg', title: 'Marchés Publics & DAO', subtitle: 'Maximisez vos chances de remporter les appels d\'offres' }
];

const stats = [
  { number: '10+', label: "Années d'expérience" },
  { number: '500+', label: 'Clients satisfaits' },
  { number: '1000+', label: 'Dossiers traités' },
  { number: '24/7', label: 'Disponibilité' },
];

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  const featuredServices = allServices.slice(0, 4);

  return (
    <>
      {/* Hero Section avec Carrousel */}
      <div className="hero-carousel">
        {carouselImages.map((image, index) => (
          <div
            key={index}
            className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
            style={{ backgroundImage: `url(${image.src})` }}
          />
        ))}
        <div className="carousel-overlay" />
        
        <Container maxWidth="lg" className="carousel-content">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Box className="hero-content">
                <Typography 
                  variant="h1" 
                  gutterBottom 
                  sx={{ 
                    fontSize: { xs: '2rem', md: '3.5rem' },
                    background: 'linear-gradient(135deg, #fff, #ffb74d)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  AUJAA GROUP
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ 
                  background: 'linear-gradient(135deg, #ffb74d, #fff)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb:2,
                }}>
                  Clinique Juridique - Expertise & Confiance
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
                  Votre partenaire juridique au Togo et pour la diaspora.
                  Services complets en droit des affaires, immobilier, recouvrement de créances,
                  marchés publics et prestations diverses.
                </Typography>
                
                <Box sx={{ mb: 4 }}>
                  {[
                    'Expertise juridique reconnue',
                    'Accompagnement personnalisé',
                    'Solutions rapides et efficaces',
                    'Services adaptés à la diaspora'
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <CheckCircle sx={{ mr: 1.5, color: '#ff9800', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    size="large" 
                    component={Link}
                    to="/consultation"
                    sx={{ 
                      borderRadius: 50,
                      px: 4,
                      '&:hover': { transform: 'translateY(-3px)' },
                    }}
                  >
                    Demander une consultation
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="large"
                    component={Link}
                    to="/contact"
                    sx={{ 
                      color: 'white', 
                      borderColor: 'white',
                      borderRadius: 50,
                      px: 4,
                      '&:hover': {
                        borderColor: '#ff9800',
                        backgroundColor: 'rgba(255,152,0,0.1)',
                        transform: 'translateY(-3px)',
                      },
                    }}
                  >
                    Nous contacter
                  </Button>
                </Box>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 5 }}>
            <Box 
              sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                animation: 'float 3s ease-in-out infinite',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: 200, md: 280 },
                  height: { xs: 200, md: 280 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    background: 'rgba(255, 255, 255, 0.15)',
                    border: '2px solid rgba(255, 152, 0, 0.5)',
                  },
                }}
              >
                <img 
                  src="/logo2.png" 
                  alt="Logo AUJAA GROUP"
                  style={{ 
                    width: '80%',
                    height: 'auto',
                    maxWidth: '180px',
                    display: 'block',
                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))',
                  }} 
                />
                {/* Anneau décoratif */}
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: '1px solid rgba(255,152,0,0.3)',
                    animation: 'rotateIn 10s linear infinite',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    width: '110%',
                    height: '110%',
                    borderRadius: '50%',
                    border: '1px dashed rgba(255,152,0,0.2)',
                    animation: 'rotateIn 15s linear infinite reverse',
                  }}
                />
              </Box>
            </Box>
          </Grid>
          </Grid>
        </Container>

        {/* Contrôles du carrousel */}
        <IconButton 
          onClick={prevSlide}
          sx={{ 
            position: 'absolute', 
            left: 20, 
            top: '50%', 
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(0,0,0,0.1)',
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.3)' }
          }}
        >
          <NavigateBefore />
        </IconButton>
        <IconButton 
          onClick={nextSlide}
          sx={{ 
            position: 'absolute', 
            right: 20, 
            top: '50%', 
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(0,0,0,0.1)',
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.3)' }
          }}
        >
          <NavigateNext />
        </IconButton>

        {/* Dots */}
        <div className="carousel-dots">
          {carouselImages.map((_, index) => (
            <div
              key={index}
              className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
            />
          ))}
        </div>
      </div>

      <Container maxWidth="lg" sx={{ mt: 6, mb: 6 }}>

        {/* Stats Section */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
        {stats.map((stat, index) => (
          <Grid size={{ xs: 6, sm: 3 }} key={index}>
            <Box 
              className="animate-on-scroll"
              sx={{ 
                textAlign: 'center',
                p: 3,
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(26,35,126,0.05), rgba(255,152,0,0.05))',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  background: 'linear-gradient(135deg, rgba(26,35,126,0.1), rgba(255,152,0,0.1))',
                },
              }}
            >
              <Typography 
                variant="h3" 
                fontWeight="bold"
                sx={{ 
                  background: 'linear-gradient(135deg, #1a237e, #ff9800)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {stat.number}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stat.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

        {/* Galerie d'images - Services en images */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h3" 
            gutterBottom 
            align="center" 
            className="animate-on-scroll"
            sx={{ mb: 1, fontWeight: 'bold' }}
          >
            Nos Services en Images
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Découvrez nos domaines d'intervention à travers cette galerie
          </Typography>
          
          <div className="gallery-grid">
            {galleryImages.map((image, index) => (
              <div key={image.id} className="gallery-item animate-on-scroll" style={{ animationDelay: `${index * 100}ms` }}>
                <img src={image.src} alt={image.title} />
                <div className="gallery-overlay">
                  <h3>{image.title}</h3>
                  <p>{image.description}</p>
                </div>
              </div>
            ))}
          </div>
        </Box>

        {/* Services à la une */}
        <Typography 
          variant="h3" 
          gutterBottom 
          align="center" 
          className="animate-on-scroll"
          sx={{ mb: 1, fontWeight: 'bold' }}
        >
          Nos Prestations
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Découvrez une sélection de nos services phares
        </Typography>
        
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {featuredServices.map((service, index) => (
            <Grid size={{ xs: 12, md: 3 }} key={service.id}>
              <div className="animate-on-scroll" style={{ animationDelay: `${index * 100}ms` }}>
                <Paper
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: 8,
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={service.image}
                    alt={service.title}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                    }}
                  />
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Box sx={{ color: service.color, mb: 2, display: 'flex', justifyContent: 'center' }}>
                      {service.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {service.shortDesc}
                    </Typography>
                    <Button 
                      component={Link} 
                      to={service.link}
                      variant="outlined"
                      size="small"
                      sx={{ 
                        borderRadius: 50,
                        '&:hover': {
                          backgroundColor: service.color,
                          color: 'white',
                          borderColor: service.color,
                        },
                      }}
                    >
                      En savoir plus <ArrowForward sx={{ fontSize: 16, ml: 0.5 }} />
                    </Button>
                  </Box>
                </Paper>
              </div>
            </Grid>
          ))}
        </Grid>

        {/* CTA */}
        <Paper
          className="animate-on-scroll"
          sx={{
            p: { xs: 4, md: 6 },
            background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
            color: 'white',
            borderRadius: 4,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Typography variant="h4" fontWeight="bold" sx={{ 
            background: 'linear-gradient(135deg, #fff, #ffb74d)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb:2,
          }}>
            Besoin d'un service personnalisé ?
          </Typography>
          <Typography variant="h6" paragraph sx={{ mb: 3, opacity: 0.9 }}>
            Nos experts sont disponibles pour analyser votre situation et vous proposer la meilleure solution
          </Typography>
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            color="secondary"
            size="large"
            sx={{ 
              borderRadius: 50,
              px: 4,
              '&:hover': { transform: 'translateY(-3px)' },
            }}
          >
            Contactez-nous dès maintenant
          </Button>
        </Paper>
      </Container>
    </>
  );
};

export default Home;